/**
 * Claim risk-engine. Single source of truth for two distinct questions:
 *
 *   1. SERVICE-CONNECTION RISK — can this claim get granted at all?
 *      Driven by: current disability + in-service event + nexus/link, plus
 *      whether a Secondary theory points at a real primary condition.
 *
 *   2. RATING / SEVERITY RISK — assuming it gets granted, will the rating
 *      reflect how bad it actually is? Driven by severity/function evidence,
 *      functional-impact paragraph quality, and rating-tier estimate.
 *
 * The engine is theory-aware. For Direct / BDD claims the in-service medical
 * record IS the nexus, so attached `element: 'service'` evidence (STR, active-
 * duty diagnosis, pre-discharge medical record) counts as covering the nexus
 * pillar — the same way the workspace-level BDD waiver does.
 *
 * Contradiction audit: if a pillar has attached evidence, the engine cannot
 * count it as missing unless every item is flagged stale / irrelevant / weak
 * / notAccepted. Stale-only pillars resolve to "needs review", not "missing".
 *
 * Pure functions only — no React, no API calls. Reused by:
 *   • Claim Tracker UI (Simple + Advanced views)
 *   • Master PDF Claim Risk section
 *   • Future per-claim share-link risk callout
 */
import { evidenceElements } from "./claimData";
import { nexusRequirementWaived, nexusWaiver } from "./nexusWaiver";

// ---- Evidence-validity ----

/**
 * Is this evidence item "valid" for the purpose of covering a pillar?
 *
 * Invalid (does NOT cover) when explicitly marked:
 *   • stale === true
 *   • irrelevant === true
 *   • notAccepted === true
 *   • strength === "weak" (case-insensitive)
 *
 * Defaults to valid. All other fields are ignored at this layer.
 */
export function isEvidenceValid(ev) {
  if (!ev) return false;
  if (ev.stale === true) return false;
  if (ev.irrelevant === true) return false;
  if (ev.notAccepted === true) return false;
  if ((ev.strength || "").toLowerCase() === "weak") return false;
  return true;
}

function pillarItems(issue, elementId, evidence) {
  return (evidence || []).filter((ev) => ev.issueIds?.includes(issue.id) && ev.element === elementId);
}

function validPillarItems(issue, elementId, evidence) {
  return pillarItems(issue, elementId, evidence).filter(isEvidenceValid);
}

// ---- Nexus coverage (theory-aware) ----

/**
 * Resolve how the nexus pillar is covered for this issue. Three ways:
 *   1. Workspace waiver (BDD or FDC-within-1yr)            → source: "waiver"
 *   2. Direct/BDD/presumptive theory + valid in-service ev → source: "inservice-evidence"
 *   3. Explicit nexus-pillar evidence attached             → source: "attached"
 *
 * Otherwise covered=false.
 */
export function getNexusCoverage(issue, evidence = [], ctx = {}) {
  // 1. Regulatory waiver (BDD / FDC within 1 yr)
  if (nexusRequirementWaived(ctx?.workspace, ctx?.master526ez)) {
    const w = nexusWaiver(ctx?.workspace, ctx?.master526ez);
    return {
      covered: true,
      source: "waiver",
      reason: w.shortReason || "Waived by claim posture",
      longReason: w.reason,
    };
  }
  // 2. Direct / BDD / Presumptive theory: in-service evidence IS the nexus
  const theory = (issue?.theory || "").toLowerCase();
  const directLike = theory.includes("direct") || theory.includes("bdd") || theory.includes("presumptive");
  if (directLike) {
    const inService = validPillarItems(issue, "service", evidence);
    if (inService.length > 0) {
      return {
        covered: true,
        source: "inservice-evidence",
        reason: "Covered by in-service documentation",
        longReason: "Direct service connection: the attached in-service evidence (STR, active-duty diagnosis, SHA entry, or pre-discharge medical record) IS the nexus link. No external medical opinion required.",
      };
    }
  }
  // 3. Explicit nexus-pillar evidence
  const nexusEv = validPillarItems(issue, "nexus", evidence);
  if (nexusEv.length > 0) {
    return {
      covered: true,
      source: "attached",
      reason: "Nexus evidence attached",
      longReason: "An explicit nexus letter or continuity-of-symptoms record is on file.",
    };
  }
  return { covered: false, source: null, reason: null, longReason: null };
}

// ---- Pillar coverage with contradiction audit ----

/**
 * Three-state resolution per pillar. Returns one of:
 *   • "have"   — pillar is covered by valid evidence (or a nexus waiver/in-service link)
 *   • "stale"  — pillar has attached items but every one is flagged stale/weak/etc.
 *   • "need"   — no attached items at all
 *
 * "stale" is intentionally distinct from "need" so the UI can say
 * "Needs review — current evidence is too old" rather than the false
 * "missing" label.
 */
export function resolvePillarCoverage(issue, elementId, evidence = [], ctx = {}) {
  if (elementId === "nexus") {
    const cov = getNexusCoverage(issue, evidence, ctx);
    if (cov.covered) return { state: "have", source: cov.source, reason: cov.reason };
    const anyAttached = pillarItems(issue, "nexus", evidence);
    if (anyAttached.length > 0) return { state: "stale", source: null, reason: "Nexus items on file but all marked stale/weak" };
    return { state: "need", source: null, reason: null };
  }
  const valid = validPillarItems(issue, elementId, evidence);
  if (valid.length > 0) return { state: "have", source: "attached", reason: null };
  const anyAttached = pillarItems(issue, elementId, evidence);
  if (anyAttached.length > 0) return { state: "stale", source: null, reason: "All attached items marked stale/weak/not-accepted" };
  return { state: "need", source: null, reason: null };
}

// ---- Secondary-theory completeness ----

/**
 * Is this issue's theory "Secondary" with no primary condition selected?
 * Accepts either a string label (`secondaryTo`) or an explicit id
 * (`secondaryToIssueId`) pointing to another issue in this workspace.
 */
export function secondaryMissingPrimary(issue, allIssues = []) {
  const theory = (issue?.theory || "").toLowerCase();
  if (!theory.includes("secondary")) return false;
  const id = (issue.secondaryToIssueId || "").trim?.() || "";
  if (id && allIssues.find((i) => i.id === id && i.id !== issue.id)) return false;
  const label = (issue.secondaryTo || "").trim();
  if (label) return false;
  return true;
}

// ---- Risk-level mapping ----

function levelFromScore(score) {
  if (score >= 3) return "High";
  if (score >= 1) return "Moderate";
  return "Low";
}

// ---- Recommended Next Upload ----

function buildRecommendedUploads(coverage, scCovered, issue) {
  // SC is solid but severity needs strengthening — push the rating-evidence list
  if (scCovered && coverage.severity.state !== "have") {
    return [
      "Personal statement",
      "Buddy statement",
      "Symptom log",
      "Recent treatment note",
      "Medication list",
      "Work impact evidence",
    ];
  }
  // Otherwise lead with the biggest SC gap
  const list = [];
  if (coverage.current.state !== "have") list.push("Recent VA or civilian diagnosis note");
  if (coverage.service.state !== "have") list.push("Service treatment record (STR) page");
  if (coverage.nexus.state !== "have") list.push("Medical nexus opinion (\"at least as likely as not\")");
  if (coverage.severity.state !== "have") list.push("Symptom log entries");
  list.push("Personal statement (VA Form 21-10210)");
  // De-dup while preserving order
  return Array.from(new Set(list)).slice(0, 6);
}

// ---- Main analyzer ----

/**
 * Run the full risk engine for one issue. Returns:
 *
 *   {
 *     serviceConnection: { level, reasons: [...], covered: bool },
 *     rating:            { level, reasons: [...], severityCovered: bool },
 *     coverage:          { current, service, nexus, severity },  // { state, reason }
 *     nexus:             getNexusCoverage(...)  output,
 *     needsPrimaryForSecondary: bool,
 *     summaryHeadline:   string,   // user-facing top line
 *     detailMessage:     string,   // prose explanation
 *     recommendedNextUpload: [...] // ordered list of evidence types to add next
 *     mentalHealth:      bool      // surfaces mental-health rating helper
 *   }
 */
export function analyzeIssueRisk(issue, allIssues = [], evidence = [], ctx = {}) {
  const coverage = {};
  evidenceElements.forEach((el) => {
    coverage[el.id] = resolvePillarCoverage(issue, el.id, evidence, ctx);
  });

  const scReasons = [];
  const ratingReasons = [];
  let scScore = 0;
  let ratingScore = 0;

  // --- Service-connection side ---
  if (coverage.current.state === "need") {
    scScore += 2;
    scReasons.push({ kind: "missing-current", label: "Missing current disability evidence" });
  } else if (coverage.current.state === "stale") {
    scScore += 1;
    scReasons.push({ kind: "stale-current", label: "Current disability evidence needs review" });
  }
  if (coverage.service.state === "need") {
    scScore += 2;
    scReasons.push({ kind: "missing-service", label: "Missing in-service event evidence" });
  } else if (coverage.service.state === "stale") {
    scScore += 1;
    scReasons.push({ kind: "stale-service", label: "In-service evidence needs review" });
  }
  if (coverage.nexus.state === "need") {
    scScore += 2;
    scReasons.push({ kind: "missing-link", label: "Missing medical link" });
  } else if (coverage.nexus.state === "stale") {
    scScore += 1;
    scReasons.push({ kind: "stale-nexus", label: "Nexus evidence needs review" });
  }
  // Secondary theory with no primary outranks everything else
  const secondaryGap = secondaryMissingPrimary(issue, allIssues);
  if (secondaryGap) {
    scScore += 3;
    scReasons.push({
      kind: "secondary-incomplete",
      label: "Secondary theory incomplete — select the primary service-connected condition or change theory to Direct / BDD.",
    });
  }

  // --- Rating side ---
  if (coverage.severity.state === "need") {
    ratingScore += 2;
    ratingReasons.push({ kind: "missing-severity", label: "Weak severity / function evidence" });
  } else if (coverage.severity.state === "stale") {
    ratingScore += 1;
    ratingReasons.push({ kind: "stale-severity", label: "Severity evidence needs review" });
  }
  const impactLen = (issue.functionalImpact || "").trim().length;
  if (impactLen === 0) {
    ratingScore += 1;
    ratingReasons.push({ kind: "no-impact", label: "No functional-impact paragraph written" });
  } else if (impactLen < 60) {
    ratingScore += 1;
    ratingReasons.push({ kind: "vague-impact", label: "Functional-impact paragraph is too short for a strong rating" });
  }
  if (!Number(issue.estimatedRating || 0)) {
    ratingScore += 1;
    ratingReasons.push({ kind: "no-rating", label: "Estimated rating tier not set" });
  }

  const scLevel = levelFromScore(scScore);
  const ratingLevel = levelFromScore(ratingScore);
  const scCovered =
    coverage.current.state === "have" &&
    coverage.service.state === "have" &&
    coverage.nexus.state === "have" &&
    !secondaryGap;

  // --- Headline / detail ---
  let summaryHeadline;
  let detailMessage;
  if (secondaryGap) {
    summaryHeadline = "Secondary theory incomplete";
    detailMessage =
      "You marked this as secondary to another service-connected condition, but no primary condition is selected. Pick the primary service-connected condition this is secondary to — or switch the theory to \"Direct / BDD\".";
  } else if (scCovered && coverage.severity.state === "have") {
    summaryHeadline = "Package looks complete";
    detailMessage =
      "Service connection appears supported and severity/function evidence is on file. Re-read every draft statement, run the C&P prep, and confirm with a VSO or accredited rep before filing.";
  } else if (scCovered && coverage.severity.state !== "have") {
    summaryHeadline = "Rating risk — service connection looks supported";
    detailMessage =
      "Service connection appears supported based on current disability and in-service documentation. Rating strength is limited because severity/function evidence is missing. Add personal statement, buddy statement, symptom log, recent treatment notes, medication history, and work-impact evidence before export.";
  } else {
    summaryHeadline = "Service connection risk";
    const gaps = scReasons
      .filter((r) => r.kind !== "secondary-incomplete")
      .map((r) => r.label);
    detailMessage = gaps.length
      ? `Service connection is not yet fully supported: ${gaps.join("; ")}. Close those gaps before filing.`
      : "Service connection is not yet fully supported — review the missing pillars.";
  }

  return {
    serviceConnection: { level: scLevel, reasons: scReasons, covered: scCovered },
    rating: { level: ratingLevel, reasons: ratingReasons, severityCovered: coverage.severity.state === "have" },
    coverage,
    nexus: getNexusCoverage(issue, evidence, ctx),
    needsPrimaryForSecondary: secondaryGap,
    summaryHeadline,
    detailMessage,
    recommendedNextUpload: buildRecommendedUploads(coverage, scCovered, issue),
    mentalHealth: (issue?.system || "").toLowerCase() === "mental",
  };
}
